import { createContext, useContext, useEffect, useState, useCallback } from "react";

const HyperlinkContext = createContext();

export const useHyperlink = () => {
    const context = useContext(HyperlinkContext);
    if(!context) {
        throw new Error('useHyperlink must be used within a HyperlinkProvider');
    }
    return context;
}

const FALLBACK_CONFIG = {
    websites: {
        angelai: { aliases: ['AngelAi', 'AngelAi.com'], link: 'https://www.swmc.com/angelai/' },
        swmc: { aliases: ['Sun West', 'Sun West Mortgage Company, Inc.'], link: 'https://www.swmc.com/' },
        celligence: { aliases: ['Celligence LLC', 'Celligence'], link: 'https://www.celligence.com' }
    },
    profiles: {
        pavan: { aliases: ['Pavan Agarwal'], link: 'https://www.swmc.com/pavan' }
    }
};

const buildLink = (text, url) =>
    `<a href="${url}" target="_blank" rel="noopener noreferrer" style="color:inherit;text-decoration:none">${text}</a>`;

const injectStyle = (html) => {
    if (!html || typeof html !== 'string') return html;
    return html.replace(/<a\s/g, '<a style="color:inherit;text-decoration:none" ');
};

export const HyperlinkProvider = ({children}) => {
    const [hyperlinkFunctions, setHyperLinkFunctions] = useState(null);

    useEffect(() => {
        const load = async () => {
            try {
                const response = await fetch(process.env.REACT_APP_HYPERLINK_PATH + '?t=' + Date.now());
                const jsCode = await response.text();
                const func = new Function(jsCode + '; return {getHyperLink, addHyperlinks};');
                setHyperLinkFunctions(func());
            } catch(e) {
                console.error('Failed to load hyperlink utils:', e);
            }
        };
        load();
    },[]);

    const getHyperLink = useCallback(async (type, id, aliasIndex) => {
        if (hyperlinkFunctions) {
            const result = await hyperlinkFunctions.getHyperLink(type, id, aliasIndex);
            return injectStyle(result);
        }
        const item = FALLBACK_CONFIG[type]?.[id];
        if (item && item.aliases[aliasIndex]) {
            return buildLink(item.aliases[aliasIndex], item.link);
        }
        return null;
    }, [hyperlinkFunctions]);

    const addHyperlinks = useCallback(async (text) => {
        if (hyperlinkFunctions) {
            const result = await hyperlinkFunctions.addHyperlinks(text);
            return injectStyle(result);
        }
        return text;
    }, [hyperlinkFunctions]);

    return (
        <HyperlinkContext.Provider value={{ getHyperLink, addHyperlinks }}>
            {children}
        </HyperlinkContext.Provider>
    );
};
